
import {
  BackupScheduleDto,
  BackupScheduleId,
  TaskScheduleDto,
  TaskScheduleRepositoryInterface,
} from '@/services/Backup/interfaces/index';
import { AxiosError } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import DeleteBackupScheduleDialog from '@/components/dialogs/backups/DeleteBackupScheduleDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  components: {
    DeleteBackupScheduleDialog,
    PageHeader,
  },
  inject: [
    'taskScheduleRepository',
  ],
})
export default class AllTaskSchedules extends Vue {
  public loading: boolean = false;
  public options: ListPaginationOptions = defaultListOptions();
  public totalItems: number = 0;
  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  public taskScheduleRepository: TaskScheduleRepositoryInterface;

  public deleteDialog: string = '';
  public deleteDialogData: BackupScheduleDto | null = null;

  public backupSchedules: TaskScheduleDto[] = [];

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Target Server', value: 'targetServerId' },
    { text: 'Interval Days', value: 'intervalDays' },
    { text: 'Run At Hour', value: 'runAtHour' },
    { text: 'Run At Minute', value: 'runAtMinute' },
    { text: 'Last Run Day', value: 'lastRunDay' },
    { text: 'Retries', value: 'retries' },
    { text: 'Created At', value: 'createdAt' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public searchOptions = {};

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = buildListPaginationQuery(this.options);
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'schedule_list', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async initialize(): Promise<void> {
    await this.search();
  }

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.initialize();
  }

  public async search(): Promise<void> {
    this.loading = true;
    try {
      await this.getBackupSchedules();
    } catch (err) {
      this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  public async getBackupSchedules(): Promise<void> {
    const result = await this.taskScheduleRepository.searchTaskSchedules(undefined, {
      page: this.options.page,
      pageSize: effectivePageSize(this.options.itemsPerPage),
    });

    this.totalItems = result?.total ?? 0;
    this.backupSchedules = (result?.data ?? []).map(x => ({
      ...x,
      idComposite: x.type + "_" + x.id,
    }));
  }

  public async onPageChange(): Promise<void> {
    await this.syncRouteQuery();
    await this.search();
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.search();
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'schedule_list') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.applyRouteQuery();
    await this.search();
  }

  public async showDeleteDialogFor(id: BackupScheduleId): Promise<void> {
    try {
      const backupSchedule = this.backupSchedules.find(x => x.id === id);
      if (!backupSchedule) {
        return;
      }

      this.deleteDialog = 'delete-backup-schedule-dialog';
      this.deleteDialogData = backupSchedule;
    } catch (err) {
      this.onError(err);
    }
  }

  public async createTaskSchedule(): Promise<void> {
    await this.$router.push({ name: 'schedule_create' });
  }

  public async deleteBackupSchedule(backupSchedule: BackupScheduleDto): Promise<void> {
    try {
      await this.backupScheduleRepository.deleteBackupSchedule(backupSchedule.id);

      this.$successDialog.show('Backup schedule deleted successfully');

      await this.search();
    } catch (err) {
      this.onError(err);
    }
  }

  private onError(err: unknown): void {
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
